import axiosNextGen from '../../../config/axios-nextgen';

/**
 * Patient Tracking API Service
 * Handles patient tracking contact types and actions
 */

// ============================================================================
// Contact Types Endpoints
// ============================================================================

/**
 * Get all contact types ordered by name
 * @returns {Promise<Array<{contactTypeId: string, contactTypeName: string, daysUntilFollowUp: number|null, requiresNonCompliant: boolean}>>}
 */
export async function getAllContactTypes() {
    const response = await axiosNextGen.get('/api/patient-tracking/contact-types');
    return response.data;
}

/**
 * Get a specific contact type by ID
 * @param {string} id - The contact type ID (GUID)
 * @returns {Promise<{contactTypeId: string, contactTypeName: string, daysUntilFollowUp: number|null, requiresNonCompliant: boolean}>}
 */
export async function getContactTypeById(id) {
    const response = await axiosNextGen.get(`/api/patient-tracking/contact-types/${id}`);
    return response.data;
}

// ============================================================================
// Tracking Actions Endpoints
// ============================================================================

/**
* Create a new patient tracking action
* @param {Object} data - The tracking action data
* @param {string} data.patientExamId - Patient exam ID (GUID)
* @param {string} [data.patientResultId] - Patient result ID (GUID, optional � pass most recent result ID if available)
* @param {string} data.contactTypeId - Contact type ID (GUID)
 * @param {string} [data.contactedByUserId] - User ID who made contact (GUID, optional)
 * @param {string} [data.facilityId] - Facility ID (GUID, optional)
 * @param {string} [data.notes] - Notes (optional)
 * @param {string} [data.nextFollowUpDate] - Next follow-up date (DateOnly format, optional)
 * @param {string} [data.nonCompliantTrackingNumber] - Non-compliant tracking number (optional, max 100 chars)
 * @param {string} [data.nonCompliantAttachment] - Base64 encoded attachment (optional)
 * @param {string} [data.nonCompliantAttachmentFileName] - Attachment filename (optional, max 255 chars)
 * @returns {Promise<Object>} The created tracking action
 */
export async function createTrackingAction(data) {
    const response = await axiosNextGen.post('/api/patient-tracking/actions', data);
    return response.data;
}

/**
 * Get all tracking actions for a specific patient exam (ordered by contact date descending)
 * @param {string} patientExamId - The patient exam ID (GUID)
 * @returns {Promise<Array<Object>>} Array of tracking actions ordered by contact date (newest first)
 */
export async function getActionsByPatientExam(patientExamId) {
    const response = await axiosNextGen.get(`/api/patient-tracking/actions/patient-exam/${patientExamId}`);
    return response.data;
}

/**
 * Get a specific tracking action by ID
 * @param {string} id - The tracking action ID (GUID)
 * @returns {Promise<Object>} The tracking action
 */
export async function getActionById(id) {
    const response = await axiosNextGen.get(`/api/patient-tracking/actions/${id}`);
    return response.data;
}

/**
 * Update an existing tracking action (most-recent only; API returns 400 otherwise)
 * @param {string} id - The tracking action ID (GUID)
 * @param {Object} data - The updated tracking action data
 * @param {string} data.contactTypeId - Contact type ID (GUID)
 * @param {string} [data.contactedByUserId] - User ID who made contact (GUID, optional)
 * @param {string} [data.facilityId] - Facility ID (GUID, optional)
 * @param {string} [data.notes] - Notes (optional)
 * @param {string} [data.nextFollowUpDate] - Next follow-up date (DateOnly format, optional)
 * @param {string} [data.nonCompliantTrackingNumber] - Non-compliant tracking number (optional, max 100 chars)
 * @param {string} [data.nonCompliantAttachment] - Base64 encoded attachment (optional)
 * @param {string} [data.nonCompliantAttachmentFileName] - Attachment filename (optional, max 255 chars)
 * @returns {Promise<Object>} The updated tracking action
 */
export async function updateTrackingAction(id, data) {
    const response = await axiosNextGen.put(`/api/patient-tracking/actions/${id}`, data);
    return response.data;
}

/**
 * Delete a tracking action (most-recent only; API returns 400 otherwise)
 * @param {string} id - The tracking action ID (GUID)
 * @returns {Promise<void>}
 */
export async function deleteTrackingAction(id) {
    await axiosNextGen.delete(`/api/patient-tracking/actions/${id}`);
}

/**
 * Download the non-compliant letter attachment for a tracking action
 * @param {string} id - The tracking action ID (GUID)
 * @returns {Promise<Blob>} The file blob with response headers containing filename
 */
export async function downloadAttachment(id) {
    const response = await axiosNextGen.get(`/api/patient-tracking/actions/${id}/attachment`, {
        responseType: 'blob'
    });
    return response;
}

// ============================================================================
// Default Export
// ============================================================================

const patientTrackingService = {
    getAllContactTypes,
    getContactTypeById,
    createTrackingAction,
    getActionsByPatientExam,
    getActionById,
    updateTrackingAction,
    deleteTrackingAction,
    downloadAttachment
};

export default patientTrackingService;


